import React, { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import UserPanelTemplate from './UserPanelTemplate';
import { ADMIN_ROUTE_MAP, ADMIN_SECTIONS } from './userPanelAdminData';
import useTivigoDashboardData from './hooks/useTivigoDashboardData';
import { contentApi, userApi } from './services/tivigoApi';
import { canRestrictedAccountOpenRoute, getAccountAccessState, hasExpiredResellerAccess } from './accountAccess';
import './mobileFontOverrides.css';

const ADMIN_THEME_PREVIEW = false;

const readSetupDeviceFromPath = (pathname) => {
  if (pathname === '/dashboard/sunucu-bilgilerim') return 'server-info';
  if (pathname === '/dashboard/m3u-olustur') return 'm3u';

  const detailMatch = pathname.match(/^\/dashboard\/kurulum-detay\/([^/]+)\/?$/);
  if (!detailMatch) return null;

  try {
    return decodeURIComponent(detailMatch[1]);
  } catch (error) {
    return detailMatch[1];
  }
};

const resolveActiveSection = (pathname) => {
  const adminSection = ADMIN_SECTIONS.find((section) => section.route === pathname);
  if (adminSection) return adminSection.id;
  if (pathname === '/dashboard/webtv') return 'live';
  if (pathname === '/dashboard/filmler') return 'movies';
  if (pathname === '/dashboard/diziler') return 'series';
  if (pathname === '/dashboard/alt-kullanicilar') return 'user-actions';
  if (pathname.startsWith('/dashboard/kurulum') || pathname === '/dashboard/sunucu-bilgilerim' || pathname === '/dashboard/m3u-olustur') return 'setup';
  if (pathname === '/dashboard/destek') return 'support';
  if (pathname === '/dashboard/referanslar') return 'bonus';
  return 'overview';
};

function LiveDashboard({ userInfo, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedSection = resolveActiveSection(location.pathname);
  const initialAccess = getAccountAccessState(userInfo);
  // Süresi dolan hesapta istenen bölüm korunur: bayi ise alt kullanıcı verileri yüklenir (hook kendi içinde süzer).
  const liveData = useTivigoDashboardData(
    initialAccess.isRestricted && initialAccess.reason !== 'expired' ? 'overview' : requestedSection,
    userInfo,
  );
  const dashboardUser = liveData.userInfo || userInfo;
  const accountAccess = getAccountAccessState(dashboardUser);
  const isAdminUser = Number(dashboardUser?.user_info?.is_admin) === 1;
  const isReseller = liveData.resellerInfo?.is_reseller === true || Number(liveData.resellerInfo?.is_reseller) === 1;
  const expiredResellerAccess = hasExpiredResellerAccess(accountAccess, isReseller);
  // Süresi dolan hesabın bayi olup olmadığı gelene kadar yönlendirme kararı verilmez.
  const expiredResellerPending = accountAccess.reason === 'expired' && !isReseller
    && !liveData.resellerState.loaded && !liveData.resellerState.error;
  const initialSetupDevice = readSetupDeviceFromPath(location.pathname);

  const bindings = useMemo(() => ({
    onCreateSubUser: liveData.actions.createSubUser,
    onRemoveSubUser: liveData.actions.removeSubUser,
    onCreateTicket: liveData.actions.createTicket,
    onReplyTicket: liveData.actions.replyTicket,
    onEditTicketReply: liveData.actions.editTicketReply,
    onSetTicketStatus: liveData.actions.setTicketStatus,
    onClaimReferralBonus: liveData.actions.claimReferralBonus,
    onLoadAccount: userApi.loadAccount,
    onUpdateProfile: (payload) => liveData.actions.updateAccount(userApi.updateProfile, payload),
    onChangePassword: async (payload) => {
      const result = await liveData.actions.updateAccount(userApi.changePassword, payload);
      if (!payload?.userInfo?._targetUserId && typeof window !== 'undefined') {
        try {
          const credentials = JSON.parse(window.localStorage.getItem('xtream_credentials') || '{}');
          window.localStorage.setItem('xtream_credentials', JSON.stringify({
            ...credentials,
            password: payload.newPassword,
          }));
          const storedUser = JSON.parse(window.localStorage.getItem('xtream_user') || '{}');
          window.localStorage.setItem('xtream_user', JSON.stringify({
            ...storedUser,
            user_info: { ...(storedUser.user_info || {}), password: payload.newPassword },
          }));
        } catch (error) {
          // Sunucu işlemi başarılıysa bozuk yerel önbellek işlemi geri alınmaz.
        }
      }
      return result;
    },
    onUpdateEmail: (payload) => liveData.actions.updateAccount(userApi.updateEmail, payload),
    onSendEmailCode: (payload) => userApi.sendEmailChangeCode(payload),
    onSendPhoneCode: (payload) => userApi.sendPhoneChangeCode(payload),
    onUpdatePhone: (payload) => liveData.actions.updateAccount(userApi.updatePhone, payload),
    onLoadTrustedDevices: userApi.listTrustedDevices,
    onRemoveTrustedDevice: userApi.removeTrustedDevice,
    onLoadLoginHistory: () => userApi.listLoginHistory({ limit: 20 }),
    onReportLogin: (payload) => userApi.reportUnauthorizedLogin(payload),
    onLoadBouquets: userApi.listBouquets,
    onUpdateBouquets: userApi.updateBouquets,
    onFetchSecurityLogs: userApi.getSecurityLogs,
    onFetchTransactionHistory: userApi.getTransactionHistory,
    onCreditPaymentResult: async (result) => {
      if (result?.success) await liveData.actions.refreshUser({ silent: true });
    },
    onPurchaseDuration: liveData.actions.purchaseDuration,
    onValidateDurationCoupon: userApi.validateCoupon,
    onLoadMovieDetail: contentApi.getMovieDetail,
    onLoadSeriesDetail: contentApi.getSeriesDetail,
    onLoadComments: userApi.getComments,
    onToggleCommentHelpful: userApi.toggleCommentHelpful,
    onSubmitReview: (payload) => userApi.addComment({
      pageType: payload.sayfaTipi,
      pageRef: payload.sayfaRef,
      comment: payload.yorum,
      rating: payload.rating,
    }),
    onLoadTicket: liveData.actions.loadTicket,
    onLoadResellerTestLimit: userApi.getResellerTestLimit,
  }), [liveData.actions]);

  const dashboardRoutes = {
    overview: '/dashboard',
    live: '/dashboard/webtv',
    movies: '/dashboard/filmler',
    series: '/dashboard/diziler',
    'user-actions': '/dashboard/alt-kullanicilar',
    setup: '/dashboard/kurulum',
    support: '/dashboard/destek',
    bonus: '/dashboard/referanslar',
    ...ADMIN_ROUTE_MAP,
  };

  const handleLogout = () => {
    onLogout?.();
  };

  const renderDashboard = (initialActiveItem) => (
    <UserPanelTemplate
      userInfo={dashboardUser}
      onLogout={handleLogout}
      initialActiveItem={initialActiveItem}
      initialSetupTargetUser={initialActiveItem === 'setup' ? location.state?.setupTargetUser || null : null}
      initialSetupDevice={initialActiveItem === 'setup' ? initialSetupDevice : null}
      initialSetupSourceAppId={initialActiveItem === 'setup' ? location.state?.sourceAppId || null : null}
      initialSeriesPlatform={initialActiveItem === 'series' ? location.state?.seriesPlatform || null : null}
      initialAdminFilter={initialActiveItem.startsWith('admin-') ? location.state?.adminFilter || null : null}
      movies={liveData.movies}
      moviesLoading={liveData.moviesState.loading || liveData.movies == null}
      series={liveData.series}
      seriesLoading={liveData.seriesState.loading || liveData.series == null}
      liveTv={liveData.liveTv}
      liveTvLoading={liveData.liveTvState.loading || liveData.liveTv == null}
      subUsersData={liveData.subUsers}
      subUsersLoading={liveData.subUsersState.loading || liveData.subUsers == null}
      supportTickets={liveData.supportTickets}
      supportLoading={liveData.supportState.loading || liveData.supportTickets == null}
      supportUnreadCount={liveData.supportStats?.unread_replies || 0}
      referrals={liveData.referralData?.referrals || []}
      referralCode={liveData.referralData?.referral_code || ''}
      referralsLoading={liveData.referralState.loading || liveData.referralData == null}
      isReseller={isReseller}
      resellerInfo={liveData.resellerInfo}
      {...bindings}
      onNavigateSection={(id, options) => navigate(dashboardRoutes[id] || '/dashboard', options)}
      onNavigateSetupDetail={(device, options = {}) => {
        const normalizedDevice = String(device || '').trim();
        const targetPath = normalizedDevice === 'server-info'
          ? '/dashboard/sunucu-bilgilerim'
          : normalizedDevice === 'm3u'
            ? '/dashboard/m3u-olustur'
            : `/dashboard/kurulum-detay/${encodeURIComponent(normalizedDevice)}`;
        const hasExplicitState = Object.prototype.hasOwnProperty.call(options, 'state');

        navigate(targetPath, {
          ...options,
          state: hasExplicitState ? options.state : location.state,
        });
      }}
      searchPreview={false}
      creditPreview={false}
      durationPreview={false}
      adminPreview={ADMIN_THEME_PREVIEW}
    />
  );

  const renderAdminDashboard = (initialActiveItem) => (
    !accountAccess.isRestricted && (isAdminUser || ADMIN_THEME_PREVIEW)
      ? renderDashboard(initialActiveItem)
      : <Navigate to="/dashboard" replace />
  );

  const renderGuardedDashboard = (initialActiveItem) => {
    if (!accountAccess.isRestricted || canRestrictedAccountOpenRoute(initialActiveItem)) {
      return renderDashboard(initialActiveItem);
    }
    // Yalnızca süresi dolan bayiye açık bölümler; kurulum sayfası sadece bir alt kullanıcı için açılır.
    const resellerSection = canRestrictedAccountOpenRoute(initialActiveItem, true)
      && (initialActiveItem !== 'setup' || Boolean(location.state?.setupTargetUser));
    if (!resellerSection) return <Navigate to="/dashboard" replace />;
    // Adres korunur; bayi bilgisi gelince aynı rota yeniden değerlendirilir.
    if (expiredResellerPending) return renderDashboard('overview');
    return expiredResellerAccess ? renderDashboard(initialActiveItem) : <Navigate to="/dashboard" replace />;
  };

  return (
    <Routes>
      <Route index element={renderDashboard('overview')} />
      <Route path="webtv" element={renderGuardedDashboard('live')} />
      <Route path="diziler" element={renderGuardedDashboard('series')} />
      <Route path="filmler" element={renderGuardedDashboard('movies')} />
      <Route
        path="alt-kullanicilar"
        element={(isReseller || liveData.resellerState.loading || !liveData.resellerState.loaded)
          ? renderGuardedDashboard('user-actions')
          : <Navigate to="/dashboard" replace />}
      />
      <Route path="kurulum" element={renderGuardedDashboard('setup')} />
      <Route path="destek" element={renderGuardedDashboard('support')} />
      <Route path="referanslar" element={renderGuardedDashboard('bonus')} />
      {ADMIN_SECTIONS.map((section) => (
        <Route
          key={section.id}
          path={section.route.replace(/^\/dashboard\/?/, '')}
          element={renderAdminDashboard(section.id)}
        />
      ))}
      <Route path="kullanicilar" element={<Navigate to="/dashboard/alt-kullanicilar" replace />} />
      <Route path="kurulum-rehberi" element={<Navigate to="/dashboard/kurulum" replace />} />
      <Route path="sunucu-bilgilerim" element={renderGuardedDashboard('setup')} />
      <Route path="m3u-olustur" element={renderGuardedDashboard('setup')} />
      <Route path="kurulum-detay/:device" element={renderGuardedDashboard('setup')} />
      <Route path="destek-alin" element={<Navigate to="/dashboard/destek" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default LiveDashboard;
